import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CardActions, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  WorkOutlined,
  AccountTreeOutlined,
  WarningOutlined,
  SecurityOutlined,
  ArrowForwardRounded,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import OrganizationHeader from "../organization/components/OrganizationHeader";

export default function PtwSettings() {
  const { t } = useTranslation();
  const theme = useTheme();

  const formDataOptions = [
    {
      title: t("ptwSettings.workFlows", { defaultValue: "Work Flows" }),
      description: t("ptwSettings.workFlowsDesc", {
        defaultValue:
          "Manage existing permit to work workflows, review processes, and configure approval chains.",
      }),
      icon: <AccountTreeOutlined sx={{ fontSize: 36 }} />,
      path: "/riskregister/workflows",
      color: theme.palette.primary.main,
      aria: t("ptwSettings.workFlowsAria", {
        defaultValue: "Manage work flows",
      }),
    },
    {
      title: t("ptwSettings.hazardsList", {
        defaultValue: "Hazards - Precautions",
      }),
      description: t("ptwSettings.hazardsListDesc", {
        defaultValue:
          "Define and manage potential workplace hazards for risk assessment and safety planning.",
      }),
      icon: <WarningOutlined sx={{ fontSize: 36 }} />,
      path: "/riskregister/hazards",
      color: theme.palette.error.main,
      aria: t("ptwSettings.hazardsListAria", {
        defaultValue: "Manage hazards",
      }),
    },
    {
      title: t("ptwSettings.precautionsList", {
        defaultValue: "Precautions & Categories",
      }),
      description: t("ptwSettings.precautionsListDesc", {
        defaultValue:
          "Configure safety precautions, protective measures, and organize them into categories for different work scenarios.",
      }),
      icon: <SecurityOutlined sx={{ fontSize: 36 }} />,
      path: "/riskregister/precautions",
      color: theme.palette.info.main,
      aria: t("ptwSettings.precautionsListAria", {
        defaultValue: "Manage precautions and categories",
      }),
    },
  ];

  return (
    <>
      <OrganizationHeader
        icon={WorkOutlined}
        title={t("ptwSettings.headerTitle", {
          defaultValue: "Permit to Work Settings",
        })}
        subtitle={t("ptwSettings.headerSubtitle", {
          defaultValue:
            "Configure workflows, safety protocols, and risk management parameters",
        })}
      />

      <Box sx={{ px: 3 }}>
        {/* PTW Settings Section */}
        <Box>
          <Grid container spacing={3}>
            {formDataOptions.map((option, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: theme.shadows[8],
                      borderColor: option.color,
                      "& .option-icon": {
                        transform: "scale(1.1)",
                        color: option.color,
                      },
                      "& .option-arrow": {
                        transform: "translateX(8px)",
                      },
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Box
                        className="option-icon"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 70,
                          height: 70,
                          borderRadius: 3,
                          bgcolor: alpha(option.color, 0.1),
                          border: "2px solid",
                          borderColor: alpha(option.color, 0.2),
                          color: alpha(option.color, 0.7),
                          transition: "all 0.3s ease",
                          mb: 2,
                        }}
                      >
                        {option.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ mb: 1, color: "text.primary", fontSize: "1rem" }}
                      >
                        {option.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.5,
                          fontSize: "0.8125rem",
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      component={Link}
                      to={option.path}
                      variant="contained"
                      fullWidth
                      endIcon={
                        <ArrowForwardRounded
                          className="option-arrow"
                          sx={{
                            fontSize: 18,
                            transition: "transform 0.3s ease",
                          }}
                        />
                      }
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        background: `linear-gradient(135deg, ${option.color} 0%, ${alpha(option.color, 0.8)} 100%)`,
                        "&:hover": {
                          boxShadow: theme.shadows[6],
                          background: `linear-gradient(135deg, ${alpha(option.color, 0.9)} 0%, ${alpha(option.color, 0.7)} 100%)`,
                        },
                        transition: "all 0.3s ease",
                      }}
                      aria-label={option.aria}
                    >
                      {t("ptwSettings.manage", { defaultValue: "Manage" })}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
}
